import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { ArrowLeft, Download, Package } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import pako from 'pako'
import { useTranslation } from 'react-i18next'

interface Release {
  id: number
  version: string
  release_notes: string | null
  installer_file_name: string
  installer_file_path: string
  installer_file_size: number
  installer_mime_type: string
  is_compressed: boolean | null
  original_file_size: number | null
  is_split: boolean | null
  total_chunks: number | null
  created_at: string
}

export const ReleaseNotesPage: React.FC = () => {
  const { t } = useTranslation()
  const { version } = useParams<{ version: string }>()
  const navigate = useNavigate()
  const { error: showError } = useToast()
  const [release, setRelease] = useState<Release | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (version) {
      fetchRelease(version)
    }
  }, [version])

  const fetchRelease = async (versionParam: string) => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('releases')
        .select('*')
        .eq('version', versionParam)
        .single()

      if (error) throw error
      setRelease(data)
    } catch (err: any) {
      console.error('Error fetching release:', err)
      showError(err.message || t('wordRa.loadFailed'))
      navigate('/pentest-releases')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!release) return

    try {
      const bucketName = 'releases'
      let fileToDownload: Blob
      const fileName = release.installer_file_name

      // if file is split, download and reassemble chunks
      if (release.is_split && release.total_chunks) {
        // fetch all chunks
        const { data: chunksData, error: chunksError } = await supabase
          .from('release_file_chunks')
          .select('*')
          .eq('release_id', release.id)
          .order('chunk_index', { ascending: true })

        if (chunksError) throw chunksError
        if (!chunksData || chunksData.length !== release.total_chunks) {
          throw new Error('Not all chunks found')
        }

        // download all chunks
        const chunkBlobs: Blob[] = []
        for (const chunk of chunksData) {
          const { data, error } = await supabase.storage
            .from(bucketName)
            .createSignedUrl(chunk.chunk_file_path, 3600)

          if (error || !data) throw error ?? new Error('Failed to sign release chunk')

          const response = await fetch(data.signedUrl)
          const blob = await response.blob()
          chunkBlobs.push(blob)
        }

        // reassemble chunks
        const chunks = chunkBlobs.map(blob => blob.arrayBuffer())
        const chunkBuffers = await Promise.all(chunks)
        const totalSize = chunkBuffers.reduce((sum, buf) => sum + buf.byteLength, 0)
        const combined = new Uint8Array(totalSize)
        
        let offset = 0
        for (const buffer of chunkBuffers) {
          combined.set(new Uint8Array(buffer), offset)
          offset += buffer.byteLength
        }

        fileToDownload = new Blob([combined], { type: 'application/gzip' })
      } else {
        // single file download
        const { data, error } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(release.installer_file_path, 3600)

        if (error || !data) throw error ?? new Error('Failed to sign release installer')

        const response = await fetch(data.signedUrl)
        fileToDownload = await response.blob()
      }

      // decompress if needed
      if (release.is_compressed) {
        const arrayBuffer = await fileToDownload.arrayBuffer()
        const decompressed = pako.ungzip(new Uint8Array(arrayBuffer))
        fileToDownload = new Blob([decompressed], { type: 'application/x-msdownload' })
      }

      // download the file
      const url = URL.createObjectURL(fileToDownload)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('Error downloading release:', error)
      showError(error.message || t('wordRa.downloadFailed'))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">{t('releasesPage.loadingNotes')}</p>
        </div>
      </div>
    )
  }

  if (!release) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('releasesPage.notFound')}</h1>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            </Button>
        </div>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{t('releasesPage.notesTitle', { version: release.version })}</CardTitle>
                <CardDescription className="mt-2">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    <span className="font-mono text-sm">{release.installer_file_name}</span>
                  </div>
                </CardDescription>
              </div>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                {t('releasesPage.downloadInstaller')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {release.release_notes ? (
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown>{release.release_notes}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-slate-500 dark:text-slate-400">{t('releasesPage.noNotes')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

